
import dataModel from '../../models/DataModel';
import UserUtil from '../user/UserUtil';
import connectivityHeartbeat from '../ConnectivityHeartbeat';
import unreadAnnouncements from '../Announcements/UnreadAnnouncements';
import Metadata from '../metadata/Metadata';

import SessionsService from '../../services/sessionsService';

import ErrorConstants from '../../constants/ErrorConstants';
import { setInsightsOnlySessionFromUser, clearInsightsOnlySession } from '../user/insightsRoleAccess';

export default {
  reissueToken,
  logoff,
  login,
  showIntroOverlays,
  initializeCompany,
  handleForeignSessionChange,
  isLoginInProgress,
};

// Guards against overlapping storage events (a foreign login writes both
// `token` and `loggedin_user_id`, so the handler can fire twice) running the
// teardown more than once.
let handlingForeignSession = false;

// Cross-tab "login in progress" lock. Auth lives in shared localStorage, and
// the Login screen's constructor deletes the shared token/loggedin_user_id on
// mount. When a foreign session change bounces THIS window to the login
// screen, that deletion would yank the token out from under a login that is
// still in flight in the OTHER window. While the lock is fresh, a window
// arriving at the login screen must leave the shared token alone.
const LOGIN_LOCK_KEY = 'loginInProgress';
const LOGIN_LOCK_TTL_MS = 30000;

function markLoginInProgress() {
  try {
    localStorage[LOGIN_LOCK_KEY] = String(Date.now());
  }
  catch (err) {
    // localStorage unavailable -> the lock is best-effort; skip it.
  }
}

function clearLoginInProgress() {
  try {
    delete localStorage[LOGIN_LOCK_KEY];
  }
  catch (err) {
    // ignore
  }
}

function isLoginInProgress() {
  try {
    const ts = parseInt(localStorage[LOGIN_LOCK_KEY], 10);
    // Timestamped so an abandoned/crashed login can't leave a permanent lock.
    return !!ts && (Date.now() - ts) < LOGIN_LOCK_TTL_MS;
  }
  catch (err) {
    return false;
  }
}

/*
 * Cross-tab / cross-window session guard.
 *
 * Auth is shared across every window of the browser through localStorage
 * (`token`, `loggedin_user_id`), but each window keeps its own in-memory Redux
 * store. If a different account logs in (or logs out) in another window it
 * overwrites the shared token, so this window would keep rendering the old
 * account's (possibly higher-privileged) UI while its API calls -- which read
 * `localStorage.token` fresh on every request -- start coming back 401.
 *
 * The `storage` event fires only in the OTHER windows, so when it reports a
 * change to an auth key we compare this window's in-memory user against what is
 * now in localStorage and, if they no longer match, sign this window out.
 */
function handleForeignSessionChange(event) {
  // `event.key` is null when localStorage is cleared wholesale; otherwise only
  // react to the two auth keys.
  if (event && event.key && event.key !== 'token' && event.key !== 'loggedin_user_id') {
    return;
  }

  // Multi-window is a web concern. On mobile the app is a single cordova
  // webview and its own relogin/token-refresh flow writes these same keys.
  if (dataModel.mobileBridge && dataModel.mobileBridge.isMobile) {
    return;
  }

  const currentUser = dataModel.usersDAO.currentUser;
  // No active user in this window -> nothing privileged to protect.
  if (!currentUser) {
    return;
  }

  const sessionUserId = localStorage.loggedin_user_id;
  const sessionToken = localStorage.token;
  // Still the same account with a valid token (e.g. a token reissue in another
  // window for this same user) -> harmless, leave this window alone.
  if (sessionToken && sessionUserId && +sessionUserId === +currentUser.id) {
    return;
  }

  if (handlingForeignSession) {
    return;
  }
  handlingForeignSession = true;

  // Remember -- for THIS window only (sessionStorage is per-window, unlike
  // localStorage) -- why we bounced, so the login screen can explain it.
  try {
    sessionStorage.foreignSessionSignout = '1';
  }
  catch (err) {
    // sessionStorage may be unavailable (private mode); the sign-out below
    // still happens, we just skip the explanatory message.
  }

  tearDownForeignSession();
}

function tearDownForeignSession() {
  // Stop everything that would keep hitting the API with a now-foreign token.
  dataModel.actionCableConnector.uninitialize();
  dataModel.activityDetection.deactivate();
  connectivityHeartbeat.stop();
  unreadAnnouncements.stop();
  Metadata.stop();

  // Route this window to the login screen and clear its in-memory identity so
  // no privileged UI can render.
  //
  // IMPORTANT: unlike logoff() we do NOT call SessionsService.logout() and do
  // NOT delete localStorage.token / loggedin_user_id -- those now belong to the
  // account that just logged in in the other window, and destroying that token
  // (POST /users/destroy_token) would break the other window's valid session.
  dataModel.setNeedsLogin();
  dataModel.usersDAO.currentUser = null;
  dataModel.usersDAO.clearPermissions();
  clearInsightsOnlySession();
}

// TODO: refactor to use promises
function reissueToken() {
  if (localStorage.current_company_id) {
    return new Promise((resolve, reject) => {
      SessionsService.reissueToken().then(token => {
        if (token.error) {
          if (dataModel.mobileBridge.isMobile) {
            dataModel.mobileBridge.mobileLogout();
          }
          else {
            this.logoff();
          }
          resolve(undefined);
        }
        else {
          dataModel.setLoginSucceeded();
          if (dataModel.activityDetection) {
            dataModel.activityDetection.activate(token, dataModel.handleExpiration, reissueToken);
          }
          if (dataModel.mobileBridge.isMobile) {
            dataModel.mobileBridge.setToken(token);
          }
          connectivityHeartbeat.start(dataModel);
          Metadata.start();
          resolve(token);
        }
      });
    });
  }
  else {
    return Promise.resolve(localStorage.token);
  }
}

function logoff(ignoreCurrentPage) {
  dataModel.actionCableConnector.uninitialize();
  dataModel.activityDetection.deactivate();
  connectivityHeartbeat.stop();
  unreadAnnouncements.stop();
  Metadata.stop();
  
  clearInsightsOnlySession();

  const user_id = localStorage.loggedin_user_id;

  delete localStorage.loggedin_user_id;
  
  delete localStorage.scrollPositionTasks;
  delete localStorage.scrollPosition;
  delete localStorage.scrollPositionCompletionStep;
  delete localStorage.perPage;
  delete localStorage.projectFilters;
  delete localStorage.searchStringProject;
  delete localStorage.favorite_projects;
  delete localStorage.badConnection;
  if (user_id || dataModel.stateTree.new_users.currentUser) {
    SessionsService.logout(user_id ? user_id : dataModel.stateTree.new_users.currentUser.id).then(async (result) => {
      try {
        // await dataModel.logoffStore()
        delete localStorage.token;
        dataModel.setNeedsLogin(ignoreCurrentPage);
        dataModel.usersDAO.currentUser = null;
      }
      catch (err) {
        throw err;
      }
    });
  }
  else {
    try {
      delete localStorage.token;
      dataModel.setNeedsLogin(ignoreCurrentPage);
      dataModel.usersDAO.currentUser = null;
    }
    catch (err) {
      throw err;
    }
  }
}

function login(email, password, reLogin) {

  // A fresh login in this window re-arms the cross-window guard so a later
  // foreign session change is caught again.
  handlingForeignSession = false;

  // Take the cross-tab login lock BEFORE SessionsService.login writes the
  // shared token, so another window bounced to its login screen (by this very
  // login tearing its session down) won't delete the token mid-flight.
  markLoginInProgress();

  const loginPromise = new Promise(async(resolve, reject) => {
    
    const isMobile = dataModel.mobileBridge.isMobile;
    const analyticsLoginAction = isMobile ? 'Login - Mobile' : 'Login - Web';

    let user = await SessionsService.login(email, password);
    // ERROR SCENARIOS
    if (!user) {
      dataModel.analytics.event('Session', analyticsLoginAction, 'Login failed');
      reject(new Error(ErrorConstants.LOGIN_OFFLINE));
      dataModel.setNeedsLogin();
    }
    else if (!user.token) {
      dataModel.analytics.event('Session', analyticsLoginAction, 'Login failed');
      if (user.message == 'Network Error') {
        reject(new Error(ErrorConstants.LOGIN_OFFLINE));
      }
      else if (user.error && user.error.response && user.error.response.status == 401) {
        reject(new Error(ErrorConstants.LOGIN_INCORRECT_CREDENTIALS));
      }
      else {
        if (reLogin) {
          dataModel.needsPartialLogin = true;
          reject(new Error(ErrorConstants.LOGIN_INCORRECT_CREDENTIALS));
        }
        else {
          dataModel.setNeedsLogin();
          const errorMessage = user.status == 401 ? user.data.message : ErrorConstants.LOGIN_INCORRECT_CREDENTIALS;
          reject(new Error(errorMessage));
        }
      }
    }
    else if (!user.id) {
      dataModel.setNeedsLogin();
      reject(new Error(ErrorConstants.LOGIN_UNEXPECTED));
    }
    else {
      // LOGIN SUCCEEDED
      // Set before initializeCompany(): the company bootstrap it kicks off skips the requests the
      // API denies to insights-only users, and it needs the answer before any of them are issued.
      setInsightsOnlySessionFromUser(user);

      const storeUserId = parseInt(dataModel.storeManager.getUserId(), 10);
      let storeCurrentCompanyId = null;

      localStorage.loggedin_user_id = user.id;
      if (!reLogin) {
        if (storeUserId != user.id) {
          await dataModel.logoffStore();
        }
        else {
          storeCurrentCompanyId = dataModel.storeManager.getCurrentCompanyId();
          if (storeCurrentCompanyId) {
            localStorage.current_company_id = storeCurrentCompanyId;
          }
          dataModel.collectionsDAO.markCollectionNeedsSync();
        }
        Metadata.start();
        connectivityHeartbeat.start(dataModel);
      }
      
      dataModel.analytics.event('Session', analyticsLoginAction, 'Login succeeded');

      dataModel.activityDetection.activate(user.token, dataModel.handleExpiration, reissueToken);

      if (dataModel.mobileBridge.isMobile) {
        connectivityHeartbeat.start(dataModel);
      }

      if (reLogin) {
        // coming from relogin popup
        dataModel.analytics.event('Session Management', 'Click', 'SessionManagementLogin');
        dataModel.needsPartialLogin = false;
        resolve(user);
      }
      if (!reLogin || (user.id != storeUserId)) {
        // initial login
        dataModel.setLoginSucceeded();

        initializeCompany(user, isMobile)
          .then(async (currentCompany) => {
            user = await dataModel.usersDAO.getUser(user.id, { recordDescriptor: { user: { additionalFields: ['profile_image'], role: {}, companies: {}, company_vendor_users: {} } } });
            if (user.role) {
              user.role_name = user.role.name;
            }
            if (!user.super_admin && user.companies && user.companies.length && user.companies[0].id != currentCompany.id) {
              user.isVendorUser = true;
            }
            dataModel.usersDAO.currentUser = user;

            if (!storeCurrentCompanyId) {
              dataModel.openActionCable();
            }

            dataModel.notificationsDataModel.fetchNotifications();
            resolve(user);
          })
          .catch(error => {
            reject(error);
          });
      }
    }
  });

  // Release the lock once login truly settles (success or failure). Attached
  // to the promise -- not a try/finally in the executor -- because the success
  // path resolves asynchronously from initializeCompany().then(), after the
  // executor has already returned.
  loginPromise.then(clearLoginInProgress, clearLoginInProgress);

  return loginPromise;
}

function initializeCompany(user, isMobile) {

  return new Promise((resolve, reject) => {
    const currentCompanyId = UserUtil.getCurrentCompanyId(user, isMobile);
    if (currentCompanyId) {
      unreadAnnouncements.stop();
      localStorage.current_company_id = currentCompanyId;
      unreadAnnouncements.start();
      
      return dataModel.companiesDAO.getCurrentCompany(currentCompanyId).then(company => {
        if (company.error) {
          if (company.error.response
              && company.error.response.status
              && +company.error.response.status == 404) {
            if (user
                && user.companies
                && user.companies.length > 0
                && user.companies[0].id + '' != localStorage.current_company_id) {
              // users current company not found... likely because
              // company no longer exists so try with users primary company
              localStorage.current_company_id = user.companies[0].id;
              return dataModel.companiesDAO.getCurrentCompany(localStorage.current_company_id).then(company => {
                if (company.error) {
                  reject(new Error(ErrorConstants.LOGIN_GETTING_COMPANY));
                } else {
                  dataModel.usersDAO.initializePermissions(user, company);
                  resolve(company);
                }
              });
            } else {
              reject(new Error(ErrorConstants.LOGIN_CURRENT_COMPANY_NOT_FOUND));
            }
          } else {
            reject(new Error(ErrorConstants.LOGIN_GETTING_COMPANY));
          }
        }
        else {
          dataModel.usersDAO.initializePermissions(user, company);
          resolve(company);
        }
      });
    }
    else {
      reject(new Error(ErrorConstants.LOGIN_NO_COMPANIES));
    }
  });
}

function showIntroOverlays(user) {
  return new Promise(resolve => {
    const isMobile = dataModel.mobileBridge.isMobile;
    //Non super-users on mobile should see
    if (!user.super_admin && isMobile && window.cordova && ('appVersion' in window.cordova)) {
      // Place welcome/intro overlays here as needed
      // if (Announcements.shouldAnnounce(user, "push_notifications", "push_notifications.enabled") ) {
      //   const userCopy = Object.assign({}, user)
      //   Announcements.didAnnounce(userCopy, "push_notifications")
      //   if (!userCopy.settings.push_notifications) {
      //     userCopy.settings.push_notifications = {}
      //   }
      //   userCopy.settings.push_notifications.enabled = true

      //   dataModel.usersDAO.updateUser(userCopy)
      // }
    }
    resolve();
  });
}



// WEBPACK FOOTER //
// ./src/helpers/session/SessionManager.js